import React, { useState, useMemo } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Badge,
  Button,
  FormGroup,
  Input,
  Spinner,
  Row,
  Col,
} from "reactstrap";
import { useTranslation } from "context/TranslationProvider";
import { useNotification } from "providers/NotificationProvider";
import userServices from "services/userServices";
import Pagination from "views/pages/components/Pagination";
import dayjs from "dayjs";

const CancellationRequestCard = ({ listing, type, onResolved, canUpdate = true }) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const [adminNotes, setAdminNotes] = useState("");
  const [actionLoading, setActionLoading] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const requests = useMemo(
    () => listing?.cancellationRequests || [],
    [listing?.cancellationRequests]
  );
  const hasCancellation =
    requests.length > 0 ||
    listing?.status === "cancellation_requested" ||
    listing?.status === "cancelled" ||
    listing?.cancellationReason;

  const paginatedRequests = useMemo(() => {
    const start = currentPage * rowsPerPage;
    return requests.slice(start, start + rowsPerPage);
  }, [requests, currentPage, rowsPerPage]);

  if (!hasCancellation) return null;

  // Active request is one that is pending, escalated, or traveler/sender resolution is pending
  const activeReq = requests.slice().reverse().find(
    (r) =>
      r.status === "pending" ||
      r.status === "escalated" ||
      r.travelerResolution === "pending" ||
      r.senderResolution === "pending"
  );

  const handleAdminResolve = async (resolution) => {
    if (!listing?._id) return;
    setActionLoading(resolution);
    try {
      const listingType =
        type ||
        listing?.type ||
        (listing?.packageDetails || listing?.senderId ? "Sender" : "Traveler");

      const res = await userServices.adminResolveCancellation({
        listingId: listing._id,
        listingType,
        resolution, // "approve" or "reject"
        adminNotes: adminNotes.trim(),
      });
      const data = res?.data || res;
      if (data?.success) {
        notify(
          "success",
          t(data.messageKey || "page.userListings.toast.adminResolved", data.message || "Cancellation resolved successfully.")
        );
        setAdminNotes("");
        if (onResolved) onResolved();
      } else {
        notify("error", t(data.messageKey || data.error || "toast.somethingWentWrong", data.message || "Failed to resolve cancellation."));
      }
    } catch (err) {
      console.error("Admin resolve error:", err);
      const errData = err?.response?.data || {};
      notify("error", t(errData.messageKey || errData.error || "toast.somethingWentWrong", errData.message || "Failed to resolve cancellation."));
    } finally {
      setActionLoading("");
    }
  };

  const getStatusColor = (st) => {
    switch (st) {
      case "pending":
        return "warning";
      case "escalated":
        return "danger";
      case "approved":
        return "success";
      case "rejected":
        return "secondary";
      case "resolved":
        return "primary";
      default:
        return "info";
    }
  };

  return (
    <Card className="shadow border-0 mb-3" style={{ borderLeft: "4px solid #f59e0b" }}>
      <CardHeader className="bg-transparent d-flex align-items-center justify-content-between py-3">
        <div className="d-flex align-items-center">
          <div
            className="rounded-circle mr-2 d-flex align-items-center justify-content-center"
            style={{ width: "32px", height: "32px", backgroundColor: "#fef3c7", color: "#d97706" }}
          >
            <i className="ni ni-bell-55 font-weight-bold" />
          </div>
          <h4 className="mb-0 text-warning font-weight-bold">
            {t("admin.cancellation.cardTitle", "Cancellation & Dispute Details")}
          </h4>
        </div>
        {activeReq ? (
          <Badge color={getStatusColor(activeReq.status)} className="px-2 py-1 text-uppercase">
            {activeReq.status === "escalated"
              ? t("admin.cancellation.statusEscalated", "Escalated to Support")
              : activeReq.status === "pending"
              ? t("admin.cancellation.statusPending", "Pending Review")
              : activeReq.status}
          </Badge>
        ) : (
          <Badge color={listing.status === "cancelled" ? "danger" : "secondary"} className="px-2 py-1 text-uppercase">
            {listing.status === "cancelled"
              ? t("page.userListings.status.cancelled", "Cancelled")
              : t("common.resolved", "Resolved")}
          </Badge>
        )}
      </CardHeader>

      <CardBody className="pt-2">
        {/* Latest or Active Cancellation Details */}
        {activeReq ? (
          <div className="mb-4 p-3 bg-white rounded">
            <h5 className="text-dark font-weight-600 mb-2">
              {t("admin.cancellation.activeRequestHeading", "Active Cancellation Request")}
            </h5>
            <Row className="mb-2">
              <Col sm="4" className="text-muted text-sm font-weight-600">
                {t("table.headers.reason", "Reason")}:
              </Col>
              <Col sm="8" className="text-dark font-weight-bold text-sm">
                {activeReq.reason || listing.cancellationReason || "-"}
              </Col>
            </Row>
            {activeReq.explanation && (
              <Row className="mb-2">
                <Col sm="4" className="text-muted text-sm font-weight-600">
                  {t("admin.cancellation.explanation", "Explanation")}:
                </Col>
                <Col sm="8" className="text-dark text-sm">
                  {activeReq.explanation}
                </Col>
              </Row>
            )}
            <Row className="mb-2">
              <Col sm="4" className="text-muted text-sm font-weight-600">
                {t("admin.cancellation.requestedAt", "Requested At")}:
              </Col>
              <Col sm="8" className="text-muted text-sm">
                {activeReq.requestedAt ? dayjs(activeReq.requestedAt).format("DD MMM YYYY, hh:mm A") : "-"}
              </Col>
            </Row>

            {/* Counterpart Response if any */}
            {(activeReq.senderResponse?.action || activeReq.responseReason || activeReq.respondedAt) && (
              <div className="mt-3 pt-2 border-top">
                <h6 className="text-uppercase text-muted font-weight-bold mb-2 text-xs">
                  {type?.toLowerCase() === "sender"
                    ? t("admin.cancellation.travelerResponse", "Traveler Response")
                    : t("admin.cancellation.senderResponse", "Sender Response")}
                </h6>
                <div className="d-flex align-items-center mb-1">
                  <Badge
                    color={
                      activeReq.senderResponse?.action === "approve" || activeReq.status === "approved"
                        ? "success"
                        : "danger"
                    }
                    className="mr-2"
                  >
                    {activeReq.senderResponse?.action === "approve" || activeReq.status === "approved"
                      ? t("common.approved", "Approved")
                      : t("common.declined", "Declined")}
                  </Badge>
                  <small className="text-muted">
                    {activeReq.senderResponse?.respondedAt || activeReq.respondedAt
                      ? dayjs(activeReq.senderResponse?.respondedAt || activeReq.respondedAt).format("DD MMM YYYY, hh:mm A")
                      : ""}
                  </small>
                </div>
                {(activeReq.senderResponse?.responseReason || activeReq.responseReason) && (
                  <p className="text-xs text-dark mb-0 mt-1">
                    <strong>{t("table.headers.reason", "Reason")}:</strong>{" "}
                    {activeReq.senderResponse?.responseReason || activeReq.responseReason}
                  </p>
                )}
              </div>
            )}

            {/* Requester decision if counterpart declined */}
            {(activeReq.travelerResolution && activeReq.travelerResolution !== "pending") ||
            (activeReq.senderResolution && activeReq.senderResolution !== "pending") ? (
              <div className="mt-2 pt-2 border-top">
                <h6 className="text-uppercase text-muted font-weight-bold mb-1 text-xs">
                  {type?.toLowerCase() === "sender"
                    ? t("admin.cancellation.senderDecision", "Sender Decision")
                    : t("admin.cancellation.travelerDecision", "Traveler Decision")}
                </h6>
                <p className="text-xs text-dark mb-0">
                  <Badge
                    color={
                      activeReq.travelerResolution === "fulfill" || activeReq.senderResolution === "fulfilled"
                        ? "success"
                        : "danger"
                    }
                  >
                    {activeReq.travelerResolution === "fulfill" || activeReq.senderResolution === "fulfilled"
                      ? t("admin.cancellation.agreedToFulfill", "Agreed to Fulfill")
                      : t("admin.cancellation.statusEscalated", "Escalated to Support")}
                  </Badge>
                  {(activeReq.travelerDecisionAt || activeReq.travelerResolutionAt || activeReq.senderResolutionAt) && (
                    <span className="text-muted ml-2">
                      {dayjs(
                        activeReq.travelerDecisionAt ||
                          activeReq.travelerResolutionAt ||
                          activeReq.senderResolutionAt
                      ).format("DD MMM YYYY, hh:mm A")}
                    </span>
                  )}
                </p>
                {(activeReq.travelerResolutionNote || activeReq.senderResolutionNote) && (
                  <p className="text-xs text-muted mt-1 mb-0">
                    <strong>{t("common.note", "Note")}:</strong>{" "}
                    {activeReq.travelerResolutionNote || activeReq.senderResolutionNote}
                  </p>
                )}
              </div>
            ) : null}

            {/* Admin Resolution Form if active */}
            {canUpdate && (
              <div className="mt-4 pt-3 border-top">
                <h6 className="text-uppercase text-primary font-weight-bold mb-2 text-xs">
                  {t("admin.cancellation.adminResolutionTitle", "Admin Resolution Action")}
                </h6>
                <p className="text-xs text-muted mb-2">
                  {t("admin.cancellation.adminResolutionDesc", "Review the dispute details above. Approving will finalize cancellation of the listing and associated order. Rejecting will keep the delivery order active.")}
                </p>
                <FormGroup className="mb-3">
                  <label className="form-control-label text-xs">
                    {t("admin.cancellation.notesLabel", "Admin Decision Notes (Optional)")}
                  </label>
                  <Input
                    type="textarea"
                    rows="2"
                    placeholder={t("admin.cancellation.notesPlaceholder", "Reason for admin resolution...")}
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    className="form-control-sm"
                  />
                </FormGroup>
                <div className="d-flex align-items-center gap-2">
                  <Button
                    color="danger"
                    size="sm"
                    disabled={Boolean(actionLoading)}
                    onClick={() => handleAdminResolve("approve")}
                    className="font-weight-bold"
                  >
                    {actionLoading === "approve" && <Spinner size="sm" className="mr-1" />}
                    {t("admin.cancellation.approveBtn", "Approve Cancellation")}
                  </Button>
                  <Button
                    color="secondary"
                    size="sm"
                    disabled={Boolean(actionLoading)}
                    onClick={() => handleAdminResolve("reject")}
                    className="font-weight-bold ml-2"
                  >
                    {actionLoading === "reject" && <Spinner size="sm" className="mr-1" />}
                    {t("admin.cancellation.rejectBtn", "Reject Cancellation (Resume Order)")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="p-3 bg-white border rounded mb-3">
            <Row className="mb-2">
              <Col sm="4" className="text-muted text-sm font-weight-600">
                {t("table.headers.reason", "Cancellation Reason")}:
              </Col>
              <Col sm="8" className="text-dark font-weight-bold text-sm">
                {listing.cancellationReason || "-"}
              </Col>
            </Row>
            {listing.cancellationExplanation && (
              <Row className="mb-2">
                <Col sm="4" className="text-muted text-sm font-weight-600">
                  {t("admin.cancellation.explanation", "Explanation")}:
                </Col>
                <Col sm="8" className="text-dark text-sm">
                  {listing.cancellationExplanation}
                </Col>
              </Row>
            )}
            {listing.cancelledAt && (
              <Row>
                <Col sm="4" className="text-muted text-sm font-weight-600">
                  {t("admin.cancellation.cancelledAt", "Cancelled Date")}:
                </Col>
                <Col sm="8" className="text-muted text-sm">
                  {dayjs(listing.cancelledAt).format("DD MMM YYYY, hh:mm A")}
                </Col>
              </Row>
            )}
          </div>
        )}

        {/* Append-only Request History */}
        {requests.length > 0 && (
          <div className="mt-3">
            <h6 className="text-uppercase text-muted font-weight-bold mb-2 text-xs">
              {t("admin.cancellation.historyTitle", "Cancellation Requests History")} ({requests.length})
            </h6>
            <div className="table-responsive">
              <table className="table align-items-center table-flush table-sm">
                <thead className="thead-light">
                  <tr>
                    <th scope="col" className="text-xs">{t("table.headers.date", "Date")}</th>
                    <th scope="col" className="text-xs">{t("table.headers.reason", "Reason")}</th>
                    <th scope="col" className="text-xs">{t("table.headers.status", "Status")}</th>
                    <th scope="col" className="text-xs">
                      {type?.toLowerCase() === "sender"
                        ? t("admin.cancellation.travelerResponse", "Traveler Response")
                        : t("admin.cancellation.senderResponse", "Sender Response")}
                    </th>
                    <th scope="col" className="text-xs">{t("admin.cancellation.adminResolution", "Admin Resolution")}</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRequests.map((req, idx) => {
                    const responseAction =
                      req.senderResponse?.action ||
                      req.travelerResponse?.action ||
                      (req.status === "approved" && req.respondedBy ? "approved" : "") ||
                      (req.status === "rejected" && req.responseReason ? "rejected" : "");

                    const responseNote =
                      req.senderResponse?.responseReason ||
                      req.travelerResponse?.responseReason ||
                      req.responseReason ||
                      "";

                    const adminRes =
                      typeof req.adminResolution === "object"
                        ? req.adminResolution?.resolution
                        : req.adminResolution;

                    const adminNote = req.adminNotes || req.adminResolution?.adminNotes || "";

                    const formattedResponseAction = responseAction
                      ? (responseAction.toLowerCase() === "approved" || responseAction.toLowerCase() === "approve"
                          ? t("common.approved", "Approved")
                          : responseAction.toLowerCase() === "declined" || responseAction.toLowerCase() === "rejected" || responseAction.toLowerCase() === "reject"
                          ? t("common.declined", "Declined")
                          : responseAction)
                      : "";

                    const formattedAdminRes = adminRes
                      ? (adminRes.toLowerCase() === "approved" || adminRes.toLowerCase() === "approve"
                          ? t("common.approved", "Approved")
                          : adminRes.toLowerCase() === "rejected" || adminRes.toLowerCase() === "reject"
                          ? t("common.rejected", "Rejected")
                          : adminRes)
                      : "";

                    return (
                      <tr key={req._id || idx}>
                        <td className="text-xs text-muted">
                          {req.requestedAt ? dayjs(req.requestedAt).format("DD MMM YYYY, HH:mm") : "-"}
                        </td>
                        <td className="text-xs font-weight-600 text-dark">
                          {req.reason || "-"}
                        </td>
                        <td>
                          <Badge color={getStatusColor(req.status)} className="text-xs">
                            {req.status === "escalated"
                              ? t("admin.cancellation.statusEscalated", "Escalated to Support")
                              : req.status === "pending"
                              ? t("admin.cancellation.statusPending", "Pending Review")
                              : req.status === "approved"
                              ? t("common.approved", "Approved")
                              : req.status === "rejected"
                              ? t("common.rejected", "Rejected")
                              : req.status}
                          </Badge>
                        </td>
                        <td className="text-xs">
                          {formattedResponseAction ? (
                            <span>
                              <strong className="text-capitalize">{formattedResponseAction}</strong>
                              {responseNote ? ` (${responseNote})` : ""}
                            </span>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td className="text-xs">
                          {formattedAdminRes ? (
                            <span>
                              <strong className="text-capitalize">{formattedAdminRes}</strong>
                              {adminNote ? ` - ${adminNote}` : ""}
                            </span>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination
              totalRows={requests.length}
              rowsPerPage={rowsPerPage}
              currentPage={currentPage}
              onPageChange={(page) => setCurrentPage(page)}
              onRowsPerPageChange={(size) => {
                setRowsPerPage(size);
                setCurrentPage(0);
              }}
              rowsPerPageOptions={[5, 10, 20]}
              t={t}
            />
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default CancellationRequestCard;

